import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useApp } from "../context/AppContext";
import { createOrder } from "../services/orderService";

function formatCurrency(value) {
  return new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
}

function buildWhatsAppUrl(phone, message) {
  const cleanPhone = String(phone || "").replace(/[^\d]/g, "");
  return `https://wa.me/${cleanPhone}?text=${encodeURIComponent(message)}`;
}

export default function Checkout() {
  const navigate = useNavigate();
  const { items, subtotal, clearCart } = useCart();
  const { siteSettings, siteSlug } = useApp();

  const [form, setForm] = useState({
    customer_name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    province: "",
    postal_code: "",
    notes: "",
    payment_method: "transfer_bank",
    shipping_method: "reguler",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const isCartEmpty = items.length === 0;

  const shippingCost = useMemo(() => {
    return subtotal >= 500000 ? 0 : 20000;
  }, [subtotal]);

  const grandTotal = subtotal + shippingCost;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validate = () => {
    if (isCartEmpty) return "Keranjang masih kosong.";
    if (!form.customer_name) return "Nama pelanggan wajib diisi.";
    if (!form.phone) return "Nomor HP wajib diisi.";
    if (!form.address) return "Alamat wajib diisi.";
    if (!form.city) return "Kota wajib diisi.";
    if (!form.province) return "Provinsi wajib diisi.";
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    const payload = {
      site_slug: siteSlug,
      customer_name: form.customer_name,
      customer_email: form.email,
      customer_phone: form.phone,
      customer_address: `${form.address}, ${form.city}, ${form.province}${form.postal_code ? `, ${form.postal_code}` : ""}`,
      note: form.notes,
      shipping_cost: shippingCost,
      order_source: "website",
      items: items.map((item) => ({
        product_id: item.id,
        quantity: item.quantity,
      })),
    };

    try {
      setLoading(true);
      const response = await createOrder(payload);

      const orderCode =
        response?.order_number ||
        response?.order_code ||
        response?.code ||
        response?.data?.order_number ||
        response?.data?.order_code ||
        response?.data?.code ||
        "";

      clearCart();

      navigate("/order-success", {
        replace: true,
        state: {
          orderCode,
          customerName: form.customer_name,
          totalAmount: grandTotal,
        },
      });
    } catch (err) {
      console.error(err);

      const message = [
        `Halo ${siteSettings?.siteName || "Admin"}, saya ingin checkout pesanan.`,
        "",
        `Nama: ${form.customer_name}`,
        `Email: ${form.email || "-"}`,
        `No HP: ${form.phone}`,
        `Alamat: ${form.address}, ${form.city}, ${form.province}, ${form.postal_code || "-"}`,
        `Pembayaran: ${form.payment_method}`,
        `Pengiriman: ${form.shipping_method}`,
        "",
        "Daftar Produk:",
        ...items.map(
          (item, idx) =>
            `${idx + 1}. ${item.name} x${item.quantity} = ${formatCurrency(
              item.price * item.quantity
            )}`
        ),
        "",
        `Subtotal: ${formatCurrency(subtotal)}`,
        `Ongkir: ${formatCurrency(shippingCost)}`,
        `Total: ${formatCurrency(grandTotal)}`,
        "",
        `Catatan: ${form.notes || "-"}`,
      ].join("\n");

      const waUrl = buildWhatsAppUrl(
        siteSettings?.whatsappNumber || siteSettings?.phoneNumber || "6281234567890",
        message
      );

      window.open(waUrl, "_blank");
      setError(
        err?.message ||
          "Order API gagal diproses. Checkout dialihkan ke WhatsApp."
      );
    } finally {
      setLoading(false);
    }
  };

  if (isCartEmpty) {
    return (
      <div className="min-h-screen bg-gray-50 px-4 py-10 dark:bg-gray-900">
        <div className="mx-auto max-w-3xl rounded-2xl border border-gray-200 bg-white p-8 text-center dark:border-gray-700 dark:bg-gray-800">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Checkout
          </h1>
          <p className="mt-3 text-gray-600 dark:text-gray-300">
            Keranjang masih kosong. Tambahkan produk terlebih dahulu.
          </p>
          <Link
            to="/marketplace"
            className="mt-6 inline-block rounded-xl bg-gray-900 px-5 py-3 text-sm font-semibold text-white hover:bg-gray-800 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-200"
          >
            Ke Marketplace
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-10 dark:bg-gray-900">
      <div className="mx-auto max-w-7xl">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Checkout
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-300">
            Lengkapi data pemesanan Anda.
          </p>
        </div>

        {error ? (
          <div className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300">
            {error}
          </div>
        ) : null}

        <form onSubmit={handleSubmit} className="grid gap-6 lg:grid-cols-[1.4fr,0.9fr]">
          <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              Data Pelanggan
            </h2>

            <div className="mt-6 grid gap-4 md:grid-cols-2">
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-200">
                  Nama Lengkap
                </label>
                <input
                  type="text"
                  name="customer_name"
                  value={form.customer_name}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm outline-none focus:border-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-white dark:focus:border-white"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-200">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm outline-none focus:border-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-white dark:focus:border-white"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-200">
                  Nomor HP
                </label>
                <input
                  type="text"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm outline-none focus:border-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-white dark:focus:border-white"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-200">
                  Metode Pembayaran
                </label>
                <select
                  name="payment_method"
                  value={form.payment_method}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm outline-none focus:border-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-white dark:focus:border-white"
                >
                  <option value="transfer_bank">Transfer Bank</option>
                  <option value="qris">QRIS</option>
                  <option value="cod">COD</option>
                </select>
              </div>
            </div>

            <div className="mt-4">
              <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-200">
                Alamat Lengkap
              </label>
              <textarea
                name="address"
                value={form.address}
                onChange={handleChange}
                rows="4"
                className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm outline-none focus:border-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-white dark:focus:border-white"
              />
            </div>

            <div className="mt-4 grid gap-4 md:grid-cols-3">
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-200">
                  Kota
                </label>
                <input
                  type="text"
                  name="city"
                  value={form.city}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm outline-none focus:border-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-white dark:focus:border-white"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-200">
                  Provinsi
                </label>
                <input
                  type="text"
                  name="province"
                  value={form.province}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm outline-none focus:border-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-white dark:focus:border-white"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-200">
                  Kode Pos
                </label>
                <input
                  type="text"
                  name="postal_code"
                  value={form.postal_code}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm outline-none focus:border-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-white dark:focus:border-white"
                />
              </div>
            </div>

            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-200">
                  Metode Pengiriman
                </label>
                <select
                  name="shipping_method"
                  value={form.shipping_method}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm outline-none focus:border-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-white dark:focus:border-white"
                >
                  <option value="reguler">Reguler</option>
                  <option value="same_day">Same Day</option>
                  <option value="pickup">Pickup</option>
                </select>
              </div>
            </div>

            <div className="mt-4">
              <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-200">
                Catatan
              </label>
              <textarea
                name="notes"
                value={form.notes}
                onChange={handleChange}
                rows="4"
                className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm outline-none focus:border-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-white dark:focus:border-white"
              />
            </div>
          </div>

          <div className="h-fit rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              Ringkasan Pesanan
            </h2>

            <div className="mt-6 space-y-4">
              {items.map((item) => (
                <div
                  key={item.id}
                  className="flex items-start justify-between gap-3 border-b border-gray-100 pb-3 dark:border-gray-700"
                >
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {item.name}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {item.quantity} x {formatCurrency(item.price)}
                    </p>
                  </div>
                  <div className="text-sm font-semibold text-gray-900 dark:text-white">
                    {formatCurrency(item.quantity * item.price)}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-gray-600 dark:text-gray-300">Subtotal</span>
                <span className="font-semibold text-gray-900 dark:text-white">
                  {formatCurrency(subtotal)}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-gray-600 dark:text-gray-300">Ongkir</span>
                <span className="font-semibold text-gray-900 dark:text-white">
                  {formatCurrency(shippingCost)}
                </span>
              </div>
            </div>

            <div className="mt-6 border-t border-gray-200 pt-4 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <span className="text-base font-semibold text-gray-900 dark:text-white">
                  Total
                </span>
                <span className="text-lg font-bold text-gray-900 dark:text-white">
                  {formatCurrency(grandTotal)}
                </span>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="mt-6 w-full rounded-xl bg-gray-900 px-4 py-3 text-sm font-semibold text-white hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-70 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-200"
            >
              {loading ? "Memproses Pesanan..." : "Buat Pesanan"}
            </button>

            <Link
              to="/cart"
              className="mt-3 block rounded-xl border border-gray-300 px-4 py-3 text-center text-sm font-medium text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
            >
              Kembali ke Keranjang
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}